/**
 * Sidebar colour stripes (backlog #44): a 3px left-edge stripe on chat sections and channels.
 * The palette is curated for the black sidebar. #fff200 (and anything that reads as yellow) is deliberately
 * absent: yellow stays reserved for the active and unread highlights. Colours are stored by id, so the hex
 * values can be retuned without a data migration.
 */
export const SIDEBAR_COLOURS = [
  { id: 'red', name: 'Red', hex: '#f87171' },
  { id: 'orange', name: 'Orange', hex: '#fb923c' },
  { id: 'green', name: 'Green', hex: '#4ade80' },
  { id: 'teal', name: 'Teal', hex: '#2dd4bf' },
  { id: 'sky', name: 'Sky blue', hex: '#38bdf8' },
  { id: 'blue', name: 'Blue', hex: '#60a5fa' },
  { id: 'violet', name: 'Violet', hex: '#a78bfa' },
  { id: 'pink', name: 'Pink', hex: '#f472b6' },
  { id: 'rose', name: 'Rose', hex: '#fda4af' },
  { id: 'grey', name: 'Grey', hex: '#a1a1aa' },
] as const;

export type SidebarColourId = (typeof SIDEBAR_COLOURS)[number]['id'];
/** 'none' is a personal override meaning "no stripe here, even if the workspace has a default". */
export type SidebarColourChoice = SidebarColourId | 'none';
export type SidebarColourTarget = 'section' | 'conversation';
export const SIDEBAR_COLOUR_IDS: readonly string[] = SIDEBAR_COLOURS.map(c => c.id);

/**
 * t169u: what the API says when a colour isn't in the palette (a stale page, a script, or reserved yellow). Built from
 * SIDEBAR_COLOURS, so the web menu and the API's check can never list different colours.
 */
export const sidebarColourError = () => {
  const names = SIDEBAR_COLOURS.map(c => c.name);
  return `That colour isn't in the sidebar palette. Choose ${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}. Yellow is kept for highlights.`;
};
export const isSidebarColourId = (v: unknown): v is SidebarColourId => typeof v === 'string' && SIDEBAR_COLOUR_IDS.includes(v);
export const sidebarColour = (id: string | null | undefined) => SIDEBAR_COLOURS.find(c => c.id === id) ?? null;
export const sidebarColourKey = (type: SidebarColourTarget, id: string) => `${type}:${id}`;

export interface SidebarColours {
  /** Workspace defaults, keyed by `section:<id>` / `conversation:<id>`. */
  defaults: Record<string, SidebarColourId>;
  /** The caller's own choices (override the defaults); 'none' hides a default. */
  mine: Record<string, SidebarColourChoice>;
}

export interface ResolvedSidebarColour {
  colour: (typeof SIDEBAR_COLOURS)[number] | null;
  source: 'mine' | 'default' | null;
  /** The workspace default, whether or not the caller overrides it. */
  workspaceDefault: SidebarColourId | null;
  mine: SidebarColourChoice | null;
}

/** Personal choice first, then the workspace default. */
export function resolveSidebarColour(colours: SidebarColours | null | undefined, type: SidebarColourTarget, id: string): ResolvedSidebarColour {
  const key = sidebarColourKey(type, id);
  const mine = colours?.mine[key] ?? null;
  const def = colours?.defaults[key] ?? null;
  if (mine === 'none') return { colour: null, source: 'mine', workspaceDefault: def, mine };
  if (mine) return { colour: sidebarColour(mine), source: 'mine', workspaceDefault: def, mine };
  return { colour: sidebarColour(def), source: def ? 'default' : null, workspaceDefault: def, mine: null };
}
